import { formatUsdc, USDC_DECIMALS } from "@breakout/sdk"
import { ZERO_ADDRESS } from "@breakout/shared/constants"
import type { Maybe } from "@breakout/shared/types/market"
import { useQuery } from "@tanstack/react-query"
import type { Address } from "viem"
import { erc20Abi, formatUnits } from "viem"
import { E2E_MODE, E2E_USDC_ADDRESS } from "../../lib/env"
import { useBreakoutConfig } from "../../lib/providers"

export const walletKeys = {
	all: ["wallet"] as const,
	balances: () => [...walletKeys.all, "balances"] as const,
	usdcBalance: (address: Address) => [...walletKeys.balances(), "usdc", address] as const,
	usdcAllowance: (owner: Address, spender: Address) =>
		[...walletKeys.all, "allowance", "usdc", owner, spender] as const,
} as const

interface UsdcBalanceArgs {
	walletAddress: Address
}

export function useUsdcBalance(args: Maybe<UsdcBalanceArgs>) {
	const config = useBreakoutConfig()

	return useQuery({
		queryKey: walletKeys.usdcBalance(args?.walletAddress ?? ZERO_ADDRESS),
		queryFn: async () => {
			if (!args) {
				throw new Error("Wallet address required")
			}
			const walletAddress = args.walletAddress

			// Get USDC address - use E2E override if available, otherwise use chainConfig
			const usdcAddress = E2E_MODE && E2E_USDC_ADDRESS ? E2E_USDC_ADDRESS : config.chainConfig.usdc

			// Fetch balance
			const balance = await config.publicClient.readContract({
				address: usdcAddress,
				abi: erc20Abi,
				functionName: "balanceOf",
				args: [walletAddress],
			})

			// Convert bigint balance to human-readable number (USDC has 6 decimals)
			const balanceNumber = Number(formatUnits(balance, USDC_DECIMALS))

			return {
				/** Raw balance as bigint (6 decimals) */
				balance,
				/** Human-readable balance as number (e.g., 125.45) */
				balanceNumber,
				/** Display-formatted balance string (e.g., "$125.45") */
				formattedBalance: `$${formatUsdc(balanceNumber, { symbol: false })}`,
				usdcAddress,
			}
		},
		enabled: !!args,
		staleTime: 20_000,
		refetchInterval: false,
		refetchOnWindowFocus: true, // Catches external deposits when user returns to tab
	})
}
